import { useEffect, useState, type ErrorInfo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Layout as AntLayout, Grid, Spin } from 'antd';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { Breadcrumb } from './Breadcrumb';
import { BreadcrumbProvider } from '@/hooks/useBreadcrumb';
import { GraceBanner, TrialBanner, OfflineBanner, SampleDataBanner } from '@/components/FeatureGate';
import { RouteErrorBoundary } from '@/components/shared/RouteErrorBoundary';
import type { AuthUser } from '@/redux/slices/authSlice';
import { reportClientError } from '@/services/clientError.service';
import { usePlanData } from '@/hooks/usePlanData';
import { useNotificationSocket } from '@/hooks/useNotificationSocket';
import { PosFullscreenProvider, usePosFullscreen } from '@/pages/POS/PosFullscreenContext';
import { onboardingActions, onboardingSelectors } from '@/redux/slices/onboardingSlice';
import { onboardingService } from '@/services/onboarding.service';
import { productService } from '@/services/product.service';
import { ROUTES } from '@/constants/routes';
import { shouldSkipOnboardingProgressFetch } from './onboardingProgress';
import { FaviconSync } from './FaviconSync';

const { Content } = AntLayout;
const { useBreakpoint } = Grid;

/**
 * TCR-1192 — chỉ lấy nhánh `auth`, giống hệt cách `RouteErrorBoundary.tsx` tự
 * đọc redux nội bộ (component đó không expose context ra ngoài qua `onError`,
 * chỉ có `(error, info)` — xem file đó để tránh lệch field name).
 */
interface AuthStateSlice {
  auth?: { user?: AuthUser | null };
}

function LayoutInner() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const screens = useBreakpoint();
  const isMobile = !screens.lg;
  const { posAppFullscreen } = usePosFullscreen();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  /**
   * TCR-1156 — chỉ các màn có khung cuộn nội bộ mới khoá chiều cao nội dung theo viewport:
   * POS để danh sách sản phẩm tự cuộn, Omnichat inbox để danh sách/tin nhắn tự cuộn. Cascade
   * `flex` chỉ hoạt động khi khối bọc <Outlet/> được phép co xuống, tức `min-height: 0`.
   *
   * KHÔNG bật cho mọi trang: khối bọc <Outlet/> chính là tấm nền trắng của
   * trang. Đặt `min-height: 0` toàn cục thì trên trang dài nó co lại đúng phần
   * viewport còn trống, còn nội dung tràn ra ngoài nằm trên nền xám —
   * đo thật ở /orders/:id @1920x1080: nền trắng 968px trong khi nội dung
   * 1461px, hụt gần 500px. Đây chính là lỗi đã xảy ra khi bản vá đầu tiên của
   * TCR-1156 đặt `minHeight: 0` không điều kiện.
   *
   * So khớp CHÍNH XÁC: các route con như '/pos/shifts' và '/omnichat/connections' là trang
   * danh sách dài, cần cuộn cả trang như mọi trang khác.
   *
   * TCR-2295 — Lịch ca có lưới tự cuộn (`.schedule-roster-scroll`): không khoá thì khối bọc giãn
   * theo cả nghìn dòng nhân sự, `<main>` cuộn kéo theo tab/thanh tuần/hàng tiêu đề ngày.
   */
  const normalizedPathname = pathname.replace(/\/+$/, '');
  const lockContentHeight =
    normalizedPathname === ROUTES.pos ||
    normalizedPathname === ROUTES.omnichat.inbox ||
    normalizedPathname === ROUTES.hrm.schedules;
  // Inbox Omnichat is a workspace application rather than a regular ERP page. Keep the
  // authenticated/layout providers, but let the workspace own the full viewport so its
  // rail + folder + inbox + thread + context columns match the approved mockup.
  const isOmnichatInbox = normalizedPathname === ROUTES.omnichat.inbox;
  const dispatch = useDispatch();

  const onboardingLoaded = useSelector(onboardingSelectors.loaded);
  const onboardingCompleted = useSelector(onboardingSelectors.isCompleted);
  const onboardingProgress = useSelector(onboardingSelectors.progress);
  const skipProgressFetch = shouldSkipOnboardingProgressFetch(onboardingProgress);

  // TCR-1192 — reporter lỗi FE cho RouteErrorBoundary tầng trong (xem onError bên dưới).
  const authUser = useSelector((state: AuthStateSlice) => state.auth?.user ?? null);
  const handleRouteError = (error: Error, info: ErrorInfo): void => {
    reportClientError(error, info, { tenant_id: authUser?.tenantId, user_id: authUser?.id });
  };

  // Pre-load onboarding progress from BE BEFORE any render/routing decision.
  // This blocks the UI with a Spin until Redux state is hydrated.
  useEffect(() => {
    let cancelled = false;

    if (skipProgressFetch) {
      setInitializing(false);
      return () => {
        cancelled = true;
      };
    }

    onboardingService
      .getProgress()
      .then((p) => {
        if (cancelled) return;
        dispatch(
          onboardingActions.setProgress({
            tenantId: p.tenant_id,
            tenantName: p.tenant_name,
            address: p.address,
            cityId: p.city_id,
            wardId: p.ward_id,
            phase1: {
              totalSteps: p.phase_1.total_steps,
              completedSteps: p.phase_1.completed_steps,
              skippedSteps: p.phase_1.skipped_steps,
              isCompleted: p.phase_1.is_completed,
              completedAt: p.phase_1.completed_at,
            },
            phase2: {
              totalSteps: p.phase_2.total_steps,
              completedSteps: p.phase_2.completed_steps,
              skippedSteps: p.phase_2.skipped_steps,
              isCompleted: p.phase_2.is_completed,
              completedAt: p.phase_2.completed_at,
              dismissedAt: p.phase_2.dismissed_at,
            },
          }),
        );
        if (p.phase_1.is_completed) {
          productService
            .list({ q: 'SAMPLE-RING-001', page_size: 1 })
            .then((res) => {
              if (cancelled) return;
              dispatch(onboardingActions.setHasSampleData(res.data.length > 0));
            })
            .catch(() => { });
        }
      })
      .catch(() => {
        if (cancelled) return;
        // On BE error, don't redirect to onboarding — let user continue.
        // Mark loaded with defaults so gate evaluates but doesn't block.
        dispatch(
          onboardingActions.setProgress({
            phase1: {
              totalSteps: 0,
              completedSteps: [],
              skippedSteps: [],
              isCompleted: true, // Assume completed on error to avoid redirect loop
              completedAt: null,
            },
            phase2: {
              totalSteps: 0,
              completedSteps: [],
              skippedSteps: [],
              isCompleted: true,
              completedAt: null,
              dismissedAt: null,
            },
          }),
        );
      })
      .finally(() => {
        if (!cancelled) setInitializing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, skipProgressFetch]);

  // Load + sync plan info to Redux once user is authenticated
  usePlanData();
  // Realtime notification subscription — graceful degrade nếu BE chưa wire WS
  useNotificationSocket();

  // Gate: nếu onboarding chưa hoàn thành → redirect về onboarding wizard
  // Chỉ fire SAU khi initializing=false (Redux đã hydrated)
  useEffect(() => {
    if (initializing) return;
    if (onboardingLoaded && !onboardingCompleted) {
      navigate(ROUTES.onboarding.index, { replace: true });
    }
  }, [initializing, onboardingLoaded, onboardingCompleted, navigate]);

  useEffect(() => {
    if (!isMobile) {
      setMobileSidebarOpen(false);
    }
  }, [isMobile]);

  if (initializing) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  const effectiveCollapsed = isMobile ? !mobileSidebarOpen : collapsed;

  return (
    <BreadcrumbProvider>
      <FaviconSync />
      <AntLayout style={{ height: '100vh', display: 'flex', flexDirection: 'row' }}>
        {!posAppFullscreen && !isOmnichatInbox && (
          <Sidebar
            collapsed={effectiveCollapsed}
            onCollapse={isMobile ? (v) => setMobileSidebarOpen(!v) : setCollapsed}
            isMobile={isMobile}
            onNavigate={() => {
              if (isMobile) setMobileSidebarOpen(false);
            }}
          />
        )}

        {!posAppFullscreen && !isOmnichatInbox && isMobile && mobileSidebarOpen && (
          <div
            onClick={() => setMobileSidebarOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.25)',
              zIndex: 99,
            }}
          />
        )}

        <AntLayout style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {!isOmnichatInbox && (
            <Header
              isMobile={isMobile}
              onToggleSidebar={isMobile ? () => setMobileSidebarOpen((v) => !v) : undefined}
            />
          )}
          <Content
            style={{
              padding: isOmnichatInbox ? 0 : (isMobile ? 12 : 0),
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              minWidth: 0,
              overflow: isMobile && mobileSidebarOpen ? 'hidden' : 'auto',
              overscrollBehavior: isMobile && mobileSidebarOpen ? 'contain' : undefined,
            }}
          >
            {/*
              TCR-1506 — `data-app-chrome` gom các dải báo trạng thái tài khoản + thanh
              điều hướng phụ lại một chỗ để print stylesheet ẩn đúng chúng. Nhắm
              `.ant-alert` toàn cục thì ẩn nhầm cảnh báo NGHIỆP VỤ do trang tự dựng.
            */}
            {!posAppFullscreen && !isOmnichatInbox && (
              <div data-app-chrome>
                <TrialBanner />
                <GraceBanner />
                <OfflineBanner />
                <SampleDataBanner />
                <Breadcrumb />
              </div>
            )}
            <div
              style={{
                background: isOmnichatInbox ? 'transparent' : '#fff',
                borderRadius: isOmnichatInbox ? 0 : 8,
                padding: isOmnichatInbox ? 0 : 5,
                margin: isOmnichatInbox ? 0 : '0 5px 5px 5px',
                flex: '1 1 auto',
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
                minHeight: lockContentHeight ? 0 : undefined,
              }}
            >
              {/* TCR-1189 — tầng trong: lỗi render của TRANG chỉ thay nội dung
                  khung trắng bằng màn lỗi; Sidebar/Header/Breadcrumb vẫn còn
                  nên người dùng đi tiếp được sang menu khác. */}
              <RouteErrorBoundary onError={handleRouteError}>
                <Outlet />
              </RouteErrorBoundary>
            </div>
          </Content>
        </AntLayout>
      </AntLayout>
    </BreadcrumbProvider>
  );
}

export const Layout = () => (
  <PosFullscreenProvider>
    <LayoutInner />
  </PosFullscreenProvider>
);
